import { api } from "@/redux/api";

import {
	RepairCompany,
	RepairCompanyListSchema,
	RepairEnqueueResult,
	RepairEnqueueResultSchema,
	RepairFix,
	RepairFixListSchema,
	RepairList,
	RepairListListSchema,
	RepairRun,
	RepairRunListSchema,
} from "../../pages/AdminFixes/schema/admin-fixes";

/**
 * `baseQueryWithValidation` takes a `responseSchema` but never applies it, so parsing in
 * `transformResponse` is what makes these types true at runtime — dates in particular arrive as
 * ISO strings. Same reasoning as `apiHealth.api.ts`.
 */
export const adminFixesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getRepairFixes: builder.query<RepairFix[], void>({
			query: () => ({ url: "repair/fixes" }),
			transformResponse: (raw: unknown) => RepairFixListSchema.parse(raw),
		}),
		getRepairCompanies: builder.query<RepairCompany[], void>({
			query: () => ({ url: "repair/companies" }),
			transformResponse: (raw: unknown) => RepairCompanyListSchema.parse(raw),
		}),
		getRepairLists: builder.query<RepairList[], { companyId: number }>({
			query: ({ companyId }) => ({ url: `repair/companies/${companyId}/lists` }),
			transformResponse: (raw: unknown) => RepairListListSchema.parse(raw),
		}),
		getRepairRuns: builder.query<RepairRun[], void>({
			query: () => ({ url: "repair/runs" }),
			transformResponse: (raw: unknown) => RepairRunListSchema.parse(raw),
			providesTags: ["RepairRun"],
		}),
		runRepairFix: builder.mutation<
			RepairEnqueueResult,
			// companyId/listId are omitted for an instance-wide fix, which has no tenant to name
			// (PREC-3900).
			{ fixCode: string; companyId?: number; listId?: number; dryRun: boolean }
		>({
			query: (body) => ({ url: "repair/runs", method: "POST", body }),
			transformResponse: (raw: unknown) => RepairEnqueueResultSchema.parse(raw),
			invalidatesTags: ["RepairRun"],
		}),
	}),
});

export const {
	useGetRepairFixesQuery,
	useGetRepairCompaniesQuery,
	useGetRepairListsQuery,
	useGetRepairRunsQuery,
	useRunRepairFixMutation,
} = adminFixesApi;
